import classNames from "classnames";
import { isEqual, omitBy } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { ButtonGroup, Col, Row } from "react-bootstrap";
import { endOfDay } from "date-fns";
import { EagerLoaded } from "../../common/types/base/orm";
import { FinancialAccount } from "../../common/types/financialAccount";
import { Invoice, InvoiceViewOptionGroup } from "../../common/types/invoice";
import { getStripeDashboardUrlForFinancialAccount } from "../../helpers/invoice";
import { Button } from "../general/Button/Button";
import { EntityFilter } from "../general/EntityFilter/EntityFilter";
import { TablePageHeader } from "../pageHeader/TablePageHeader";
import {
    defaultInvoiceStatuses,
    FiltersChangeFn,
    FilterTabs,
} from "../pageHeader/filters/lib";
import { DateRangeFilter } from "../general/DateRangeFilter/DateRangeFilter";
import { DATE_RANGE_PRESETS_BY_ID } from "../../helpers/date";
import { DateRange } from "../../common/helpers/date";
import {
    CustomSelect,
    CustomSelectOption,
} from "../forms/CustomSelect/CustomSelect";
import { useInvoicesView } from "../../contexts/InvoicesViewContext";
import { AccountSelectModal } from "./AccountSelectModal";
import { InvoiceDetails } from "./InvoiceDetails";
import { InvoiceStats } from "./InvoiceStats";
import { PaginatedInvoicesTableContextProvider } from "./PaginatedInvoicesTableContextProvider";
import styles from "./InvoicePageContent.module.scss";
import { InvoicesByCounterpartyTableContainer } from "./InvoicesByCounterpartyTableContainer";
import { InvoicesByCounterpartyTableContextProvider } from "./InvoicesByCounterpartyTableContextProvider";
import { InvoicesTableContainer } from "./InvoicesTableContainer";

const VIEW_OPTIONS: Array<CustomSelectOption<string | number>> = [
    {
        value: InvoiceViewOptionGroup.INVOICE,
        label: "By invoice",
    },
    {
        value: InvoiceViewOptionGroup.COUNTERPARTY,
        label: "By customer",
    },
];

interface Props {
    invoicingAccounts: FinancialAccount[];
}

export const InvoicePageContent: React.FC<Props> = ({ invoicingAccounts }) => {
    const [selectedInvoice, setSelectedInvoice] = useState<Invoice>();
    const [showAccountSelectModal, setShowAccountSelectModal] = useState(false);

    const { filters, setFilters, viewOption, setViewOption, invoiceStats } =
        useInvoicesView();

    const onCreateInvoice = useCallback(() => {
        if (invoicingAccounts.length === 1) {
            window.open(
                getStripeDashboardUrlForFinancialAccount(invoicingAccounts[0]),
                "_blank",
            );
            return;
        }

        setShowAccountSelectModal(true);
    }, [invoicingAccounts]);

    const onAccountSelect = useCallback(
        (account: FinancialAccount) => {
            setShowAccountSelectModal(false);
            window.open(
                getStripeDashboardUrlForFinancialAccount(account),
                "_blank",
            );
        },
        [setShowAccountSelectModal],
    );

    const onInvoiceClick = useCallback(
        (invoice: Invoice) => {
            setSelectedInvoice(invoice);
        },
        [setSelectedInvoice],
    );

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (value) => {
            const newFilters = omitBy(
                { ...filters, ...value },
                (v) => v === null || v === undefined,
            );

            if (isEqual(newFilters, filters)) {
                return;
            }

            setFilters(newFilters);
            setSelectedInvoice(undefined);
        },
        [filters, setFilters],
    );

    const handleDateRangeChange = useCallback(
        (value: Partial<DateRange>, isAllTime?: boolean) => {
            const endOfToday = endOfDay(new Date());
            if (isAllTime) {
                handleFiltersChange({ start: undefined, end: endOfToday });
            } else {
                handleFiltersChange({ start: value.start, end: value.end });
            }
        },
        [handleFiltersChange],
    );

    const defaultFilters = useMemo(() => {
        const today = new Date();
        const last4MonthsPreset = DATE_RANGE_PRESETS_BY_ID.last_4_months;
        return {
            invoiceStatuses: defaultInvoiceStatuses,
            start: last4MonthsPreset.getStart(today),
            end: last4MonthsPreset.getEnd(today),
        };
    }, []);

    const dateRange = useMemo(() => {
        if (filters?.start === undefined && filters?.end === undefined) {
            return {
                start: defaultFilters.start,
                end: defaultFilters.end,
            };
        }
        return {
            start: filters?.start,
            end: filters?.end,
        };
    }, [filters, defaultFilters]);

    const handleViewOptionChange = useCallback(
        (value: InvoiceViewOptionGroup) => {
            setViewOption({ groupBy: value });

            if (value === InvoiceViewOptionGroup.COUNTERPARTY) {
                setSelectedInvoice(undefined);
            }
        },
        [setViewOption, setSelectedInvoice],
    );

    return (
        <>
            <TablePageHeader
                search={filters?.search}
                onSearchChange={(value) => {
                    handleFiltersChange({ search: value ?? "" });
                }}
                filters={filters ?? {}}
                onFiltersChange={handleFiltersChange}
                defaultFilters={defaultFilters}
                hideClearButton={true}
                availableFilters={[
                    FilterTabs.INVOICE_STATUSES,
                    FilterTabs.DUE,
                    FilterTabs.AMOUNT,
                    FilterTabs.ACCOUNT,
                ]}
            >
                <ButtonGroup className={styles.buttonGroup}>
                    <DateRangeFilter
                        buttonClass={styles.dateRangeFilter}
                        dateRange={dateRange}
                        onChange={handleDateRangeChange}
                    />
                    <CustomSelect
                        className={styles.viewOptionsPopover}
                        options={VIEW_OPTIONS}
                        value={viewOption.groupBy}
                        onSelected={handleViewOptionChange}
                        dropdownKey="view-options"
                        size="xs"
                    />
                </ButtonGroup>
                <EntityFilter
                    filters={filters ?? {}}
                    onChange={handleFiltersChange}
                />
                <Button onClick={onCreateInvoice}>Create Invoice</Button>
            </TablePageHeader>

            <Col className={classNames("d-flex flex-column gap-4 p-0")}>
                <InvoiceStats
                    invoiceStats={invoiceStats}
                    filters={filters}
                    setFilters={setFilters}
                />
                <Row>
                    <Col sm={12} md={selectedInvoice ? 8 : 12}>
                        {viewOption.groupBy ===
                        InvoiceViewOptionGroup.INVOICE ? (
                            <PaginatedInvoicesTableContextProvider>
                                <InvoicesTableContainer
                                    onInvoiceClick={onInvoiceClick}
                                    selectedInvoiceId={selectedInvoice?.id}
                                />
                            </PaginatedInvoicesTableContextProvider>
                        ) : (
                            <InvoicesByCounterpartyTableContextProvider>
                                <InvoicesByCounterpartyTableContainer />
                            </InvoicesByCounterpartyTableContextProvider>
                        )}
                    </Col>
                    {selectedInvoice && (
                        <Col md={4} className="d-none d-md-block">
                            <InvoiceDetails
                                invoice={
                                    selectedInvoice as EagerLoaded<
                                        Invoice,
                                        "lines"
                                    >
                                }
                                setInvoice={setSelectedInvoice}
                            />
                        </Col>
                    )}
                </Row>
            </Col>
            <AccountSelectModal
                onHide={() => setShowAccountSelectModal(false)}
                show={showAccountSelectModal}
                invoicingAccounts={invoicingAccounts}
                onSelect={onAccountSelect}
            />
        </>
    );
};
